import { computed, ref, watch } from "vue"
import type { Ref } from "vue"
import { useGetAuthorsQuery } from "../graphql/types"
import type {
  AuthorListItemFragment,
  FindAuthorsQuery,
  GetAuthorsQuery,
  GetAuthorsQueryVariables,
  ReactiveFunction,
} from "../graphql/types"
import type { UseQueryReturn } from "@vue/apollo-composable"
import type { UseQueryOptions } from "@vue/apollo-composable"
import type { PageFragment } from "../PageFragment"
import type { OrderRaw } from "../components/order"

export const AUTHOR_ACTIVE_STATES_STORAGE_KEY        = "nfans.authors.activeStates"
export const AUTHOR_PROFILE_VISIBILITIES_STORAGE_KEY = "nfans.authors.profileVisibilities"
export const AUTHOR_LINKED_USER_SUBS_STORAGE_KEY        = "nfans.authors.linkedUserSubs"
export const AUTHOR_LINKED_USERS_INCLUDE_NONE_STORAGE_KEY = "nfans.authors.linkedUsersIncludeNone"
export const AUTHOR_ORDER_STORAGE_KEY                = "nfans.authors.order"

export const AUTHOR_ORDER_PROPERTIES: Array<OrderRaw> = [
  { label: "Name",             properties: [{ property: "name",             defaultDirection: "asc"  }] },
  { label: "Erstellt",         properties: [{ property: "createdDate",      defaultDirection: "desc" }] },
  { label: "Zuletzt geändert", properties: [{ property: "lastModifiedDate", defaultDirection: "desc" }] },
]

export type AuthorActiveStateValue = "ACTIVE" | "INACTIVE"
export type AuthorProfileVisibilityValue = "VISIBLE" | "HIDDEN"

export const AUTHOR_ACTIVE_STATE_VALUES: Set<string> = new Set<AuthorActiveStateValue>(["ACTIVE", "INACTIVE"])
export const AUTHOR_PROFILE_VISIBILITY_VALUES: Set<string> = new Set<AuthorProfileVisibilityValue>(["VISIBLE", "HIDDEN"])

export type AllAuthorsReturn = UseQueryReturn<
  GetAuthorsQuery,
  GetAuthorsQueryVariables
> & {
  allAuthors: Ref<Array<Readonly<AuthorListItemFragment>>>
}

export function useGetAllAuthors(
  options?: UseQueryOptions<GetAuthorsQuery, GetAuthorsQueryVariables> | Ref<UseQueryOptions<GetAuthorsQuery, GetAuthorsQueryVariables>> | ReactiveFunction<UseQueryOptions<GetAuthorsQuery, GetAuthorsQueryVariables>>
): AllAuthorsReturn {
  const limit = ref(1000)
  const variables = computed<GetAuthorsQueryVariables>(() => ({
    page: 0,
    limit: limit.value,
  }))
  const r = useGetAuthorsQuery(variables, options)
  const totalPages = computed(() => r.result.value?.allAuthors?.totalPages || 0)
  const allAuthors = computed(() => r.result.value?.allAuthors?.content || [])

  watch(totalPages, (totalPagesValue) => {
    if (totalPagesValue > 1) {
      limit.value = totalPagesValue * 1000  // size ONE final fetch to cover all pages (was an escalating +1000 re-fetch chain)
    }
  })

  return { ...r, allAuthors }
}

export function extractFindAuthorsPage(result: FindAuthorsQuery): PageFragment<AuthorListItemFragment> {
  return result.findAuthors
}

export function extractFindAuthorsItems(result: FindAuthorsQuery): AuthorListItemFragment[] {
  return result.findAuthors?.content ?? []
}
